import { buildDefinition } from 'sinks'
import types from './types.js'

export const caseDataDefinitionBare = {
  // version number for tracking/applying migrations
  version: 'int',

  // applied preset id
  appliedPreset: 'str',

  // identifiers
  caseId: 'str',

  // provider
  sedationProviderName: 'str',
  surgeonName: 'str',
  recorderName: 'str',
  office: 'str',

  // case info
  procedure: 'str',
  procedureDate: 'fhirDate',
  reasonForSedation: 'str',
  responsibleCompanion: 'str',
  responsibleCompanionSignature: 'str',
  sedationLevel: 'str',
  present: 'str',
  oralSedativesEnabled: 'bool',
  ivEnabled: 'bool',
  airwayEnabled: 'bool',
  userRole: 'str',
  calculateDrugWaste: 'bool',
  vitalsInterval: 'num',
  vitalsTrackingEnabled: 'bool',

  'trackedVitalTypes.{}.selected': 'bool',
  'trackedVitalTypes.{}.ignored': 'bool',
  'trackedVitalTypes.{}.hasReceivedAutoValue': 'bool',

  // patient
  firstName: 'str',
  lastName: 'str',
  nickname: 'str',
  birthDate: 'str',
  height: 'num',
  height_raw: 'str',
  weight: 'num',
  weight_raw: 'str',
  allergiesSummary: 'str',
  anesthesiaHistorySummary: 'str',
  familyAnesthesiaHistorySummary: 'str',
  surgicalHistorySummary: 'str',
  hospitalizationSummary: 'str',
  pregnant: 'str',
  pregnancyDetail: 'str',
  breastfeeding: 'bool',
  breastfeedingInstructionsGiven: 'bool',
  lastFood: 'str',
  lastDrink: 'str',
  healthHistoryId: 'str',
  healthHistoryReviewedTime: 'timestamp',
  // used when receiving a health history from the API
  // or pasted from an EMR.
  healthHistorySummary: 'str',
  // Used as additional notes for health history
  healthHistoryNotes: 'str',
  medicalConsult: 'bool',
  medicalConsultNotes: 'str',

  // medications
  medicationsText: 'str',
  medications: 'arr',
  'medications.[].id': 'str',
  'medications.[].name': 'str',

  // preop
  'preOpChecks.[]': 'str', // deprecated

  // pre-op checks
  'preOpChecklist.{}.label': 'str',
  'preOpChecklist.{}.complete': 'bool',
  'preOpChecklist.{}.order': 'num',

  // opt checks
  'optChecks.{}.label': 'str',
  'optChecks.{}.complete': 'bool',
  'optChecks.{}.order': 'num',

  // physical assessment
  lungAuscultation: 'str',
  heartAuscultation: 'str',
  preProcedureRiskAssessment: 'str',
  weightDistribution: 'str',

  // airway assessment
  mallampati: 'str',
  asaClassification: 'str',
  brodsky: 'str',
  ulbt: 'str',
  tmd: 'str',
  iid: 'str',

  // systems review
  'systemsReview.{}.hasEntries': 'bool',
  'systemsReview.{}.entries': 'arr',
  'systemsReview.{}.entries.[].editing': 'bool',
  'systemsReview.{}.entries.[].details.{}': 'str',
  'systemsReview.{}.entries.[].details.{}.name': 'str',
  'systemsReview.{}.entries.[].details.{}.label': 'str',
  'systemsReview.{}.entries.[].details.{}.path': 'str',
  'systemsReview.{}.entries.[].details.{}.value': 'str',

  // checkpoints
  'vitalsCheckpoints.{}.complete': 'bool',
  'vitalsCheckpoints.{}.time': 'timestamp',

  'vitalRecords.{}.id': 'str', // deprecated: should not be getting set anymore
  'vitalRecords.{}.vitals.{}.id': 'str', // deprecated: should not be getting set anymore
  'vitalRecords.{}.vitals.{}.value': ['str', 'num'],

  'autoVitalRecords.{}.vitals.{}.value': ['str', 'num'],
  'autoVitalRecords.{}.vitals.{}.time': 'timestamp',
  'autoVitalRecords.{}.vitals.{}.connectionId': 'str',
  'autoVitalRecords.{}.vitals.{}.monitorType': 'str',

  // manual vitals (not checkpointed)
  'mv.{}.time': 'timestamp',
  'mv.{}.vitals.sbp': 'num',
  'mv.{}.vitals.dbp': 'num',
  'mv.{}.vitals.rr': 'num',
  'mv.{}.vitals.hr': 'num',
  'mv.{}.vitals.spo2': 'num',
  'mv.{}.vitals.etco2': 'num',

  // patient measurements taken as part of health history gathering
  'pm.{}.time': 'timestamp',
  'pm.{}.vitals.sbp': 'num',
  'pm.{}.vitals.dbp': 'num',
  'pm.{}.vitals.rr': 'num',
  'pm.{}.vitals.hr': 'num',
  'pm.{}.vitals.spo2': 'num',
  'pm.{}.vitals.etco2': 'num',

  // connected monitor meta
  connectedMonitorType: 'str',
  // kept for backward compatibility (can remove later)
  lastKnowActiveBPCuffTime: 'timestamp',
  lastKnownActiveBPCuffTime: 'timestamp',

  // pre-med
  preMedsUsed: 'bool',
  'preMedsAdministered.{}.rxcui': 'str',
  'preMedsAdministered.{}.route': 'str',
  'preMedsAdministered.{}.unit': 'str',
  'preMedsAdministered.{}.editing': 'bool',
  'preMedsAdministered.{}.time': 'timestamp',
  'preMedsAdministered.{}.dosage': 'str',
  'preMedsAdministered.{}.drugName': 'str',
  'preMedsAdministered.{}.drugId': 'str',

  // drugs
  'drugsAdministered.{}.id': 'str',
  'drugsAdministered.{}.time': 'timestamp',
  'drugsAdministered.{}.name': 'str',
  'drugsAdministered.{}.drugId': 'str',
  'drugsAdministered.{}.rxcui': 'str',
  'drugsAdministered.{}.drugVariantId': 'str',
  'drugsAdministered.{}.epiConcentration': 'num',
  'drugsAdministered.{}.amount': 'num',
  'drugsAdministered.{}.amount_raw': 'str',
  'drugsAdministered.{}.shouldStartCase': 'bool',
  'drugsAdministered.{}.unit': 'str',
  'drugsAdministered.{}.route': 'drugRoute',
  'drugsAdministered.{}.editing': 'bool',
  'drugsAdministered.{}.siteId': 'str',
  'drugsAdministered.{}.bagId': 'str',

  // paren drug dosages
  'drugDosage.iv.doses.{}.time': 'timestamp',
  'drugDosage.iv.doses.{}.amount_raw': 'str',
  'drugDosage.iv.doses.{}.wasted_raw': 'str',
  'drugDosage.iv.doses.{}.drawAmountTemp_raw': 'str',
  'drugDosage.iv.doses.{}.totalDrawn_raw': 'str',
  'drugDosage.iv.doses.{}.wasteConfirmed': 'bool',
  'drugDosage.iv.doses.{}.route': 'parenDrugRoute',
  'drugDosage.iv.doses.{}.meta': 'str',

  // infusion drug dosage
  'drugDosage.infusion.doses.{}.time': 'timestamp',
  'drugDosage.infusion.doses.{}.amount_raw': 'str',
  // num deprecated as of (Dec 2022)
  'drugDosage.infusion.doses.{}.wasted_raw': ['str', 'num'],
  'drugDosage.infusion.doses.{}.used_raw': ['str', 'num'],
  'drugDosage.infusion.doses.{}.route': 'infusionRoute',
  'drugDosage.infusion.doses.{}.unit': 'str',
  'drugDosage.infusion.doses.{}.bolusUnit': 'str',
  'drugDosage.infusion.doses.{}.wasteConfirmed': 'bool',
  'drugDosage.infusion.doses.{}.totalUnit': 'str',
  'drugDosage.infusion.doses.{}.kg': 'str',
  'drugDosage.infusion.doses.{}.meta': 'str',

  // inhalation drug dosage
  'drugDosage.inhalation.doses.{}.time': 'timestamp',
  'drugDosage.inhalation.doses.{}.drugId': 'str', // not used but set on older cases
  'drugDosage.inhalation.doses.{}.amount': 'num',

  // oral sedative dosage
  'drugDosage.oral.doses.{}.time': 'timestamp',
  'drugDosage.oral.doses.{}.amount_raw': 'str',
  'drugDosage.oral.doses.{}.route': 'oralSedativeRoute',

  // local anesthetics dosage
  'drugDosage.local.doses.{}.time': 'timestamp',
  'drugDosage.local.doses.{}.amount_raw': 'str',
  'drugDosage.local.doses.{}.unit': 'str',

  // case-specific parenteral drug lookup
  // this is to make sure case data is self-contained
  // in case
  parenDrugs: 'obj',
  'parenDrugs.{}.name': 'str',
  'parenDrugs.{}.unit': 'str',

  oralSedatives: 'obj',
  'oralSedatives.{}': 'str', // drug ID
  'oralSedatives.{}.name': 'str',
  'oralSedatives.{}.unit': 'str',

  localAnesthetics: 'obj',
  'localAnesthetics.{}': 'str', // drug ID
  'localAnesthetics.{}.name': 'str',
  'localAnesthetics.{}.unit': 'str',
  'localAnesthetics.{}.rxcui': 'str',
  'localAnesthetics.{}.epiConcentration': 'num',
  'localAnesthetics.{}.variantId': 'str',
  'localAnesthetics.{}.percent': 'num',

  infusionDrugs: 'obj',
  'infusionDrugs.{}': 'str', // drug ID
  'infusionDrugs.{}.name': 'str',

  // custom LA
  'laDilutions.{}.variantId': 'str',
  // June, 2024: this should have been a number, but we may have some cases with strings. Changed June, 2024
  'laDilutions.{}.percent': ['str', 'num'],

  // procedure
  startedOnRoomAir: 'bool',
  systemsReviewSkipped: 'bool',
  monitorPlacementComplete: 'bool',
  dischargeScoreReviewed: 'bool',
  timelineReviewed: 'bool',

  // ready for review
  'rfr.time': 'timestamp',
  'rfr.name': 'str',
  'rfr.memId': 'str',

  // final details
  finalReportSignature: 'str',
  finalReportSignatureTime: 'timestamp',
  finalReportSignerName: 'str',

  // secondary signatures
  'sig.{}.name': 'str',
  'sig.{}.signature': 'str',
  'sig.{}.time': 'timestamp',

  // for vitals tracking
  'events.caseStart': 'timestamp',
  'events.vitalsEnd': 'timestamp',
  // for IV
  'events.ivStart': 'timestamp',
  'events.ivEnd': 'timestamp',
  // for surgery
  'events.surgeryStart': 'timestamp',
  'events.surgeryEnd': 'timestamp',
  // anesthesia time
  'events.anesthesiaStart': 'timestamp',
  'events.anesthesiaEnd': 'timestamp',
  // recovery and final end
  'events.recoveryStart': 'timestamp',
  'events.end': 'timestamp',

  'customEvents.{}.id': 'str', // we used to set this but it's not used anymore. Stopped using May, 2024
  'customEvents.{}.checkpointId': 'str',
  'customEvents.{}.time': 'timestamp',
  'customEvents.{}.note': 'str',

  // iv details
  'ivLocations.{}.name': 'str',
  'ivLocations.{}.side': 'str',
  'ivLocations.{}.securedWith': 'str',
  'ivLocations.{}.attempts': 'int',
  'ivLocations.{}.needleGauge': 'str',
  'ivLocations.{}.time': 'timestamp',
  'ivLocations.{}.timeRemoved': 'timestamp',
  'ivLocations.{}.editing': 'bool',
  'ivLocations.{}.solutions.{}.name': 'str',
  'ivLocations.{}.solutions.{}.time': 'timestamp',
  'ivLocations.{}.solutions.{}.timeRemoved': 'timestamp',
  'ivLocations.{}.solutions.{}.amount': 'positiveInt',
  'ivLocations.{}.solutions.{}.amount_raw': 'str',
  'ivLocations.{}.solutions.{}.amountConfirmed': 'bool',
  'ivLocations.{}.solutions.{}.meta': 'str',
  'ivLocations.{}.solutions.{}.doseAmount_raw': 'str',
  'ivLocations.{}.solutions.{}.doseType': 'str',

  // airway
  airways: 'arr',
  'airways.[].type': 'str',
  'airways.[].timeStarted': 'timestamp',
  'airways.[].timeEnded': 'timestamp',

  // meant to supersede above airways
  // airway placement
  'aw.{}.type': 'airwayType',
  'aw.{}.name': 'str',
  'aw.{}.notes': 'str',
  'aw.{}.timePlaced': 'timestamp',
  'aw.{}.placementComp': 'bool',
  'aw.{}.placementCompDetail': 'str',
  'aw.{}.vaso': 'str',
  'aw.{}.side': 'str',
  'aw.{}.size': 'str',
  'aw.{}.preOx': 'str',
  'aw.{}.magill': 'str',
  'aw.{}.scope': 'str',
  'aw.{}.peep': 'str',
  'aw.{}.maskType': 'str',
  'aw.{}.scopeType': 'str',
  'aw.{}.bladeSize': 'str',
  'aw.{}.viewGrade': 'str',
  'aw.{}.stylet': 'bool',
  'aw.{}.cuffed': 'bool',
  'aw.{}.inflated': 'bool',
  'aw.{}.securedWith': 'str',
  'aw.{}.depth': 'str',
  'aw.{}.eyeProtection': 'str',
  'aw.{}.confirmation': 'str',
  'aw.{}.lube': 'bool',
  'aw.{}.preWarmed': 'bool',
  // for face mask
  'aw.{}.sealQuality': 'str',
  'aw.{}.connectedTo': 'str',
  'aw.{}.maskHolder': 'str',

  // airway removal
  'aw.{}.cavityClear': 'bool',
  'aw.{}.timeRemoved': 'timestamp',
  'aw.{}.removalComp': 'bool',
  'aw.{}.removalCompDetail': 'str',
  'aw.{}.removalNotes': 'str',
  'aw.{}.preRemovalSpo2': 'str',
  'aw.{}.postRemovalSpo2': 'str',
  'aw.{}.deflated': 'bool',

  // waves
  'waves.{}.name': 'str',
  'waves.{}.time': 'timestamp',
  'waves.{}.type': 'str',
  'waves.{}.svg': 'waveSvg',
  'waves.{}.notes': 'str',

  // position
  posSkipped: 'bool',
  'pos.{}.name': 'str',
  'pos.{}.time': 'timestamp',

  // notes
  procedureNotes: 'str',
  procedureNotesSignature: 'str',
  otherLocalAnesthetics: 'str',
  ebl: 'positiveInt',

  // recovery
  recoveryNotes: 'str',
  recoveryNotesSignature: 'str',
  recoveryNotesSignatureTime: 'timestamp',
  finalRoomAirSpO2: 'positiveInt',

  // discharge scoring
  dischargeScoreType: 'str',
  'dischargeScoreEntries.{}.score': 'int',

  // demo / speed helpers
  'appSpeed.speed': 'positiveInt',
  'appSpeed.originalStart': 'timestamp',
  'appSpeed.realTimeAtLastAdjustment': 'timestamp',
  'appSpeed.totalExtra': 'positiveInt',

  // tour related
  tourModeEnabled: 'bool',
  tourModeMeta: 'str',

  // reporting
  separatePreOpPage: 'bool',
}

export const definition = buildDefinition(caseDataDefinitionBare, types)
